import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Product, ProductConfiguration } from '@/types'

export interface BasketItem {
  id: string
  product: Product
  configuration: ProductConfiguration
  quantity: number
  note: string
  addedAt: string
}

interface QuoteBasketState {
  items: BasketItem[]
  addItem: (product: Product, configuration: ProductConfiguration) => void
  removeItem: (id: string) => void
  updateQuantity: (id: string, quantity: number) => void
  updateConfiguration: (id: string, configuration: ProductConfiguration) => void
  updateNote: (id: string, note: string) => void
  clearBasket: () => void
  getItemCount: () => number
}

export const useQuoteBasketStore = create<QuoteBasketState>()(
  persist(
    (set, get) => ({
      items: [],

      addItem: (product, configuration) => {
        const id = `${product.id}-${Date.now()}`
        set((state) => ({
          items: [
            ...state.items,
            {
              id,
              product,
              configuration,
              quantity: 1,
              note: '',
              addedAt: new Date().toISOString(),
            },
          ],
        }))
      },

      removeItem: (id) => {
        set((state) => ({
          items: state.items.filter((item) => item.id !== id),
        }))
      },

      updateQuantity: (id, quantity) => {
        if (quantity < 1) return
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id ? { ...item, quantity } : item
          ),
        }))
      },

      updateConfiguration: (id, configuration) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id ? { ...item, configuration } : item
          ),
        }))
      },

      updateNote: (id, note) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id ? { ...item, note } : item
          ),
        }))
      },

      clearBasket: () => set({ items: [] }),

      getItemCount: () => {
        return get().items.reduce((sum, item) => sum + item.quantity, 0)
      },
    }),
    {
      name: 'engitronix-quote-basket',
    }
  )
)
